import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import departmentService from "../../services/departmentService";
import { queryKeys } from "./queryKeys";

export const useDepartments = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.departments.list(filters),
    queryFn: () => departmentService.getDepartments(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useAllDepartments = (params = {}) => {
  return useQuery({
    queryKey: [...queryKeys.departments.all, "all", params],
    queryFn: async () => {
      console.log('useAllDepartments: Making API call with params:', params);
      try {
        const response = await departmentService.getAllDepartments(params);
        console.log('useAllDepartments: Raw axios response:', response);
        console.log('useAllDepartments: Response type:', typeof response);
        console.log('useAllDepartments: Response keys:', response ? Object.keys(response) : []);
        console.log('useAllDepartments: response.data:', response?.data);
        console.log('useAllDepartments: response.data.data:', response?.data?.data);
        console.log('useAllDepartments: Is response.data an array?', Array.isArray(response?.data));
        console.log('useAllDepartments: Is response.data.data an array?', Array.isArray(response?.data?.data));
        
        // Axios automatically returns response.data, so response should be { status, message, data, pagination }
        // But if axios returns the full response object, we need response.data
        const apiData = response?.data || response;
        console.log('useAllDepartments: Extracted apiData:', apiData);
        return apiData;
      } catch (error) {
        console.error('useAllDepartments: API error:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // Reduced to 30 seconds for faster updates
    retry: 1,
    enabled: true, // Explicitly enable the query
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
};

export const useDepartment = (id) => {
  return useQuery({
    queryKey: queryKeys.departments.detail(id),
    queryFn: () => departmentService.getDepartmentById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDepartmentsByInstitute = (instituteId) => {
  return useQuery({
    queryKey: [...queryKeys.departments.all, "institute", instituteId],
    queryFn: () => departmentService.getDepartmentsByInstitute(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDepartmentStatistics = (departmentId) => {
  return useQuery({
    queryKey: [...queryKeys.departments.detail(departmentId), "statistics"],
    queryFn: () => departmentService.getDepartmentStatistics(departmentId),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDepartmentTeachers = (departmentId) => {
  return useQuery({
    queryKey: [...queryKeys.departments.detail(departmentId), "teachers"],
    queryFn: () => departmentService.getDepartmentTeachers(departmentId),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDepartmentStudents = (departmentId) => {
  return useQuery({
    queryKey: [...queryKeys.departments.detail(departmentId), "students"],
    queryFn: () => departmentService.getDepartmentStudents(departmentId),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (departmentData) =>
      departmentService.createDepartment(departmentData),
    onSuccess: async () => {
      // Invalidate all department queries so cached data is marked stale
      await queryClient.invalidateQueries({
        queryKey: queryKeys.departments.all,
      });
      // Refetch all "departments" list queries (matches useAllDepartments keys: ["departments", "all", params])
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.departments.all, "all"],
        type: "active",
      });
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, departmentData }) =>
      departmentService.updateDepartment(id, departmentData),
    onMutate: async ({ id, departmentData }) => {
      // Cancel any outgoing refetches for all department queries
      await queryClient.cancelQueries({ queryKey: ["departments"] });
      
      // Snapshot the previous value for all possible query keys
      const previousDepartmentsAll = queryClient.getQueryData(["departments", "all"]);
      const previousDepartmentsList = queryClient.getQueryData(["departments", "list"]);
      
      // Optimistically update all department queries
      const updateQuery = (old) => {
        if (!old) return old;
        
        // Handle different response structures
        let departmentsArray = [];
        if (Array.isArray(old)) {
          departmentsArray = old;
        } else if (old.data && Array.isArray(old.data)) {
          departmentsArray = old.data;
        } else if (old.departments && Array.isArray(old.departments)) {
          departmentsArray = old.departments;
        } else if (old.rows && Array.isArray(old.rows)) {
          departmentsArray = old.rows;
        }
        
        // Update the department in the array
        const updatedArray = departmentsArray.map((dept) => {
          const deptId = dept.id || dept._id;
          if (deptId === id) {
            return { ...dept, ...departmentData };
          }
          return dept;
        });
        
        // Return the same structure as the original
        if (Array.isArray(old)) {
          return updatedArray;
        } else if (old.data && Array.isArray(old.data)) {
          return { ...old, data: updatedArray };
        } else if (old.departments && Array.isArray(old.departments)) {
          return { ...old, departments: updatedArray };
        } else if (old.rows && Array.isArray(old.rows)) {
          return { ...old, rows: updatedArray };
        }
        
        return old;
      };
      
      // Update all possible department queries
      queryClient.setQueryData(["departments", "all"], updateQuery);
      queryClient.setQueriesData({ queryKey: ["departments", "all"] }, updateQuery);
      queryClient.setQueriesData({ queryKey: ["departments", "list"] }, updateQuery);
      
      return { previousDepartmentsAll, previousDepartmentsList };
    },
    onError: (err, newDepartment, context) => {
      // If the mutation fails, roll back all queries
      if (context?.previousDepartmentsAll) {
        queryClient.setQueryData(["departments", "all"], context.previousDepartmentsAll);
      }
      if (context?.previousDepartmentsList) {
        queryClient.setQueryData(["departments", "list"], context.previousDepartmentsList);
      }
    },
    onSuccess: () => {
      // Force immediate API call to get-all-departments on success
      console.log('Update successful - triggering immediate refetch...');
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.refetchQueries({ 
        queryKey: ["departments", "all"],
        exact: false
      });
    },
    onSettled: () => {
      // Always ensure fresh data from server
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.refetchQueries({ 
        queryKey: ["departments", "all"],
        exact: false,
        throwOnError: false
      });
    },
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => departmentService.deleteDepartment(id),
    onSuccess: async () => {
      // Invalidate all department queries so cached data is marked stale
      await queryClient.invalidateQueries({
        queryKey: queryKeys.departments.all,
      });
      // Refetch all "departments" list queries (matches useAllDepartments keys: ["departments", "all", params])
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.departments.all, "all"],
        type: "active",
      });
    },
  });
};

export const useAssignHOD = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ departmentId, userId }) =>
      departmentService.assignHOD(departmentId, userId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.departments.detail(variables.departmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.departments.lists(),
      });
    },
  });
};

export const useImportDepartments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileData) => departmentService.importDepartments(fileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.departments.lists(),
      });
    },
  });
};

export const useExportDepartments = () => {
  return useMutation({
    mutationFn: (params) => departmentService.exportDepartments(params),
  });
};

export const useUpdateDepartmentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) =>
      departmentService.updateDepartmentStatus(id, status),
    onSuccess: (data, variables) => {
      const { id, status } = variables;
      try {
        queryClient.setQueriesData(
          { queryKey: queryKeys.departments.all, exact: false },
          (old) => {
            if (old == null || typeof old !== "object") return old;
            const list = Array.isArray(old) ? old
              : Array.isArray(old?.data) ? old.data
              : Array.isArray(old?.departments) ? old.departments
              : Array.isArray(old?.rows) ? old.rows
              : null;
            if (!Array.isArray(list)) return old;
            const nextList = list.map((d) => {
              const did = d?.id ?? d?._id;
              if (did == null) return d;
              if (String(did) === String(id)) return { ...d, status };
              return d;
            });
            if (Array.isArray(old)) return nextList;
            if (old.data) return { ...old, data: nextList };
            if (old.departments) return { ...old, departments: nextList };
            if (old.rows) return { ...old, rows: nextList };
            return old;
          }
        );
      } catch (e) {
        // ignore cache update errors
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.departments.all,
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.departments.all, "all"],
      });
      queryClient.refetchQueries({
        queryKey: [...queryKeys.departments.all, "all"],
      });
    },
  });
};

